/**
 * End-of-clue reveal panel (#192, FR-4.10) — presentational.
 *
 * Rendered on the Player + Presentation surfaces while a clue is holding in
 * `RESOLVED` (see `clueReveal` selector), in place of the buzzer:
 *  - `unanswered` — nobody got it right (all wrong, timer expired, dismissed, or a
 *    wrong Daily Double). Shows a status pill and the correct `response` in an amber
 *    band, with a short "back to the board" countdown. The server reveals the
 *    response to all roles only during this hold (game-server `views.ts`).
 *  - `correct` — a player was awarded the points. Shows a green award cue
 *    ("✓ +$value" + the player's name); the response is neither shown nor read.
 *
 * The hold length is server-decided; this component just counts down toward
 * `reveal.resolvesAtEpochMs` for a legible "returning to the board" affordance.
 */

import { useEffect, useState } from 'react';
import type { ClueReveal as ClueRevealPayload } from '@jeopardy/shared';

export interface ClueRevealProps {
  reveal: ClueRevealPayload;
  /** The correct response — shown on the `unanswered` path only. */
  response?: string | null;
  categoryName?: string;
  value?: number;
  /** The awarded player's display name — shown on the `correct` path. */
  awardedPlayerName?: string;
  /** Injectable clock for tests (defaults to `Date.now`). */
  now?: () => number;
}

/** Whole seconds remaining until the board returns (never negative). */
function secondsLeft(resolvesAtEpochMs: number, now: () => number): number {
  return Math.max(0, Math.ceil((resolvesAtEpochMs - now()) / 1000));
}

export function ClueReveal({
  reveal,
  response,
  categoryName,
  value,
  awardedPlayerName,
  now = Date.now,
}: ClueRevealProps): React.JSX.Element {
  const [remaining, setRemaining] = useState(() =>
    secondsLeft(reveal.resolvesAtEpochMs, now),
  );

  useEffect(() => {
    setRemaining(secondsLeft(reveal.resolvesAtEpochMs, now));
    const id = setInterval(() => {
      setRemaining(secondsLeft(reveal.resolvesAtEpochMs, now));
    }, 250);
    return () => clearInterval(id);
  }, [reveal.resolvesAtEpochMs, now]);

  const header =
    value !== undefined
      ? `${categoryName ? `${categoryName} — ` : ''}$${value}`
      : categoryName;

  if (reveal.outcome === 'correct') {
    const delta = reveal.delta ?? 0;
    return (
      <div
        className="clue-panel clue-reveal clue-reveal--award"
        data-testid="clue-reveal"
        data-outcome="correct"
      >
        {header && <div className="clue-panel__value">{header}</div>}
        <div className="clue-reveal__award" data-testid="clue-reveal-award">
          <span className="clue-reveal__check" aria-hidden="true">
            ✓
          </span>{' '}
          +${delta}
        </div>
        {awardedPlayerName && (
          <div className="clue-reveal__award-name">{awardedPlayerName}</div>
        )}
      </div>
    );
  }

  return (
    <div
      className="clue-panel clue-reveal clue-reveal--unanswered"
      data-testid="clue-reveal"
      data-outcome="unanswered"
    >
      {header && <div className="clue-panel__value">{header}</div>}
      <div className="clue-reveal__pill" data-testid="clue-reveal-pill">
        Time&rsquo;s up — nobody answered
      </div>
      {response && (
        <div className="clue-reveal__response" data-testid="clue-reveal-response">
          <div className="clue-reveal__response-label">Correct response</div>
          {response}
        </div>
      )}
      <div className="clue-reveal__countdown" data-testid="clue-reveal-countdown">
        Back to the board in {remaining}s
      </div>
    </div>
  );
}
